// Copyright © 2024 Ory Corp
// SPDX-License-Identifier: Apache-2.0

import { SettingsFlow, UpdateSettingsFlowBody } from '@ory/client'
import { NodeMessages, UserSettingsCard } from '@ory/elements'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { sdk, useSdkError } from '../../services/sdk'
import { SpinnerIcon } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'

interface Props {
  showTitle?: boolean
}
export const ProfileSettings: FC<Props> = ({ showTitle = true }) => {
  const [flow, setFlow] = useState<SettingsFlow | null>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()

  const navigate = useNavigate()

  // Get the flow based on the flowId in the URL (.e.g redirect to this page after flow initialized)
  const getFlow = useCallback(
    (flowId: string) =>
      sdk
        // the flow data contains the form fields, error messages and csrf token
        .getSettingsFlow({ id: flowId })
        .then(({ data: flow }) => setFlow(flow))
        .catch(sdkErrorHandler),
    // eslint-disable-next-line
    [],
  )

  // initialize the sdkError for generic handling of errors
  const sdkErrorHandler = useSdkError(getFlow, setFlow, '/settings', true)

  const createFlow = React.useCallback(() => {
    sdk
      // create a new settings flow
      // the flow contains the form fields, error messages and csrf token
      // depending on the Ory Network project settings, the form fields returned may vary
      .createBrowserSettingsFlow()
      .then(({ data: flow }) => {
        // Update URI query params to include flow id
        setSearchParams({ ['flow']: flow.id }, { replace: true })
        // Set the flow data
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }, [sdkErrorHandler, setSearchParams])

  // submit any of the settings form data to Ory
  const onSubmit = (body: UpdateSettingsFlowBody) => {
    // something unexpected went wrong and the flow was not set
    if (!flow) return navigate('/settings', { replace: true })

    sdk
      // submit the form data the user provided to Ory
      .updateSettingsFlow({ flow: flow.id, updateSettingsFlowBody: body })
      .then(({ data: flow }) => {
        setFlow(flow)
      })
      .catch(sdkErrorHandler)
  }

  useEffect(() => {
    if (!flow) {
      // we might redirect to this page after the flow is initialized, so we check for the flowId in the URL
      const flowId = searchParams.get('flow')
      // the flow already exists
      if (flowId) {
        getFlow(flowId).catch(createFlow) // if for some reason the flow has expired, we need to get a new one
        return
      }
      createFlow()
    }
  }, [createFlow, getFlow, searchParams, flow])

  if (!flow) {
    return (
      <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10'>
        <SpinnerIcon className='w-6 h-6 text-sky-500 animate-spin' />
      </div>
    )
  }

  return (
    <div className='[&_h3]:!hidden'>
      {showTitle && <p className='text-base text-gray-800 font-semibold mb-3'>{t('aboutMe')}</p>}
      <NodeMessages uiMessages={flow.ui.messages} />
      <UserSettingsCard
        flow={flow}
        method='profile'
        includeScripts
        onSubmit={({ body }) => onSubmit(body as UpdateSettingsFlowBody)}
        className='[&_button]:!bg-blue-500 [&_button]:!rounded [&_button]:!px-4 [&_button]:!py-2 [&_button]:text-sm [&_button]:!font-normal [&_input]:!rounded-md [&_input]:!border [&_input:focus]:!border-sky-100 [&_input:focus]:!ring-0 [&_input:focus]:!ring-transparent [&_input]:!border-gray-300 [&_h3]:!hidden'
        dividerClassName='hidden'
      />
    </div>
  )
}
